import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Plus, Edit2, Trash2, Settings as SettingsIcon, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { FADE_IN_HIDDEN, FADE_IN_SHOWN, FADE_IN_UP_HIDDEN, FADE_IN_UP_SHOWN, STAGGER_TRANSITION } from '../lib/animations';
import { getAccountIcon } from '../lib/accountIcons';
import { fmtMoney } from '../lib/format';
import ConfirmDialog from '../components/ConfirmDialog';
import useUndoDelete from '../hooks/useUndoDelete';
import ExportMenu from '../components/ExportMenu';
import PageTitle from '../components/PageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Accounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const { scheduleDelete } = useUndoDelete();
  const [formData, setFormData] = useState({
    name: '',
    account_type: 'checking',
    balance: 0,
    counts_toward_shortfall: null,
  });

  const fetchData = useCallback(async () => {
    try {
      const [accRes, typesRes] = await Promise.all([
        axios.get(`${API}/accounts`, { withCredentials: true }),
        axios.get(`${API}/account-types`, { withCredentials: true }),
      ]);
      setAccounts(accRes.data);
      setTypes(typesRes.data);
    } catch (error) {
      toast.error('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const typeMap = Object.fromEntries(types.map((t) => [t.slug, t]));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAccount) {
        await axios.put(`${API}/accounts/${editingAccount.account_id}`, formData, { withCredentials: true });
        toast.success('Account updated successfully');
      } else {
        await axios.post(`${API}/accounts`, formData, { withCredentials: true });
        toast.success('Account created successfully');
      }
      fetchData();
      setDialogOpen(false);
      setEditingAccount(null);
      setFormData({ name: '', account_type: types[0]?.slug || 'checking', balance: 0, counts_toward_shortfall: null });
    } catch (error) {
      toast.error('Failed to save account');
    }
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setFormData({
      name: account.name,
      account_type: account.account_type,
      balance: account.balance,
      counts_toward_shortfall: account.counts_toward_shortfall ?? null,
    });
    setDialogOpen(true);
  };

  const handleDelete = (account) => {
    setPendingDelete(account);
  };

  const confirmDelete = () => {
    const account = pendingDelete;
    if (!account) return;
    setPendingDelete(null);
    // Optimistically remove
    setAccounts((prev) => prev.filter((a) => a.account_id !== account.account_id));
    scheduleDelete({
      id: account.account_id,
      label: 'Account',
      executor: () => axios.delete(`${API}/accounts/${account.account_id}`, { withCredentials: true }),
      onUndo: () => setAccounts((prev) => [account, ...prev]),
      onAfterDelete: fetchData,
    });
  };

  const openNewDialog = () => {
    setEditingAccount(null);
    setFormData({ name: '', account_type: types[0]?.slug || 'checking', balance: 0 });
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052FF]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-8" data-testid="accounts-page">
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <PageTitle
          title="Accounts"
          subtitle="Manage your financial accounts"
          testId="accounts-title"
          className="mb-0"
        />
        <div className="flex gap-3 flex-wrap print-hide">
          <ExportMenu
            testIdPrefix="accounts-export"
            getData={() => ({
              fileName: 'forge-ledger-accounts',
              sheetName: 'Accounts',
              columns: [
                { header: 'Name', key: 'name', width: 22 },
                { header: 'Type', key: 'type', width: 14 },
                { header: 'Kind', key: 'kind', width: 10 },
                { header: 'Balance (GBP)', key: 'balance', width: 14 },
                { header: 'Opening Balance (GBP)', key: 'opening_balance', width: 18 },
                { header: 'Shortfall Setting', key: 'shortfall', width: 18 },
                { header: 'Currency', key: 'currency', width: 10 },
              ],
              rows: accounts.map((a) => {
                const t = typeMap[a.account_type];
                const flag = a.counts_toward_shortfall;
                return {
                  name: a.name,
                  type: t?.name || a.account_type,
                  kind: t?.kind || '',
                  balance: a.balance,
                  opening_balance: a.opening_balance ?? a.balance,
                  shortfall: flag === true ? 'Include' : flag === false ? 'Exclude' : 'Default',
                  currency: a.currency || 'GBP',
                };
              }),
            })}
          />
          <Link to="/settings/account-types" className="inline-flex items-center gap-2 bg-[#F1F5F9] text-[#020617] hover:bg-[#E2E8F0] rounded-lg px-5 py-2.5 font-medium transition-colors" data-testid="manage-types-link">
            <SettingsIcon className="w-4 h-4" />
            Manage Types
          </Link>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNewDialog} className="bg-[#0052FF] text-white rounded-lg px-6 py-2.5 font-medium hover:bg-[#003ECC]" data-testid="add-account-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Account
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-[#020617]">{editingAccount ? 'Edit Account' : 'Add New Account'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="account-form">
                <div>
                  <Label htmlFor="name">Account Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="Main Checking Account"
                    className="mt-1.5"
                    data-testid="account-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="account_type">Account Type</Label>
                  <Select value={formData.account_type} onValueChange={(value) => setFormData({ ...formData, account_type: value })}>
                    <SelectTrigger className="mt-1.5" data-testid="account-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {types.map((t) => (
                        <SelectItem key={t.type_id} value={t.slug}>
                          {t.name} {t.kind === 'liability' ? '· Liability' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {typeMap[formData.account_type]?.kind === 'liability' && (
                    <p className="text-xs text-[#64748B] mt-1.5">
                      Enter the amount you owe as a positive number. This will subtract from your overall net worth.
                    </p>
                  )}
                </div>
                {!editingAccount && (
                  <div>
                    <Label htmlFor="balance">
                      {typeMap[formData.account_type]?.kind === 'liability' ? 'Amount Owed (GBP)' : 'Initial Balance (GBP)'}
                    </Label>
                    <Input
                      id="balance"
                      type="number"
                      step="0.01"
                      value={formData.balance}
                      onChange={(e) => setFormData({ ...formData, balance: parseFloat(e.target.value) || 0 })}
                      className="mt-1.5"
                      data-testid="account-balance-input"
                    />
                  </div>
                )}

                <div className="border-t border-[#E2E8F0] pt-4">
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Shortfall calculation</Label>
                  <div className="mt-2 space-y-2" data-testid="shortfall-mode-options">
                    {['default', 'include', 'exclude'].map((mode) => {
                      const currentMode = formData.counts_toward_shortfall === true ? 'include'
                        : formData.counts_toward_shortfall === false ? 'exclude'
                        : 'default';
                      const isLiability = typeMap[formData.account_type]?.kind === 'liability';
                      const labels = {
                        default: isLiability ? 'Use default (exclude from shortfall)' : 'Use default (include in shortfall)',
                        include: isLiability
                          ? 'Include — subtract this debt from the spendable-cash figure (treat as imminent expense)'
                          : 'Include — add this balance to the spendable-cash figure',
                        exclude: 'Exclude — never let this account influence the shortfall warning',
                      };
                      const valueMap = { default: null, include: true, exclude: false };
                      return (
                        <label key={mode} className={`flex items-start gap-3 cursor-pointer rounded-md p-2 transition-colors ${currentMode === mode ? 'bg-[#EFF6FF]' : 'hover:bg-[#F8FAFC]'}`}>
                          <input
                            type="radio"
                            name="shortfall-mode"
                            value={mode}
                            checked={currentMode === mode}
                            onChange={() => setFormData({ ...formData, counts_toward_shortfall: valueMap[mode] })}
                            className="mt-0.5"
                            data-testid={`shortfall-radio-${mode}`}
                          />
                          <span className="text-xs text-[#334155] leading-relaxed">{labels[mode]}</span>
                        </label>
                      );
                    })}
                  </div>
                  {typeMap[formData.account_type]?.kind === 'liability' && (
                    <p className="text-[11px] text-[#64748B] mt-2 flex items-start gap-1">
                      <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                      <span>Useful for credit cards you clear each month — tick "Include" to flag a shortfall if your assets can't cover the balance.</span>
                    </p>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="flex-1 bg-[#0052FF] text-white hover:bg-[#003ECC]" data-testid="account-submit-button">
                    {editingAccount ? 'Update' : 'Create'}
                  </Button>
                  <Button type="button" onClick={() => setDialogOpen(false)} className="flex-1 bg-[#F1F5F9] text-[#020617] hover:bg-[#E2E8F0]">
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {accounts.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-12 text-center">
          <p className="text-[#64748B] mb-4">No accounts yet. Create your first account to get started.</p>
          <Button onClick={openNewDialog} className="bg-[#0052FF] text-white hover:bg-[#003ECC]">
            <Plus className="w-4 h-4 mr-2" />
            Add Your First Account
          </Button>
        </div>
      ) : (
        <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" initial={FADE_IN_HIDDEN} animate={FADE_IN_SHOWN} transition={STAGGER_TRANSITION}>
          {accounts.map((account) => {
            const t = typeMap[account.account_type];
            const Icon = getAccountIcon(t?.icon);
            const isLiability = t?.kind === 'liability';
            return (
              <motion.div
                key={account.account_id}
                initial={FADE_IN_UP_HIDDEN}
                animate={FADE_IN_UP_SHOWN}
                className="relative bg-white border border-[#E2E8F0] rounded-lg p-6 hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:border-[#CBD5E1] transition-all duration-300 group"
                data-testid={`account-card-${account.account_id}`}
              >
                <Link
                  to={`/accounts/${account.account_id}/statement`}
                  className="absolute inset-0 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0052FF]"
                  aria-label={`Open statement for ${account.name}`}
                  data-testid={`view-statement-${account.account_id}`}
                />
                <div className="relative flex items-start justify-between mb-4 pointer-events-none">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      isLiability ? 'bg-[#FEE2E2] text-[#991B1B]' : 'bg-[#DBEAFE] text-[#1E40AF]'
                    }`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">{t?.name || account.account_type}</div>
                  </div>
                  <div className="flex gap-2 relative z-10 pointer-events-auto print-hide">
                    <button onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleEdit(account); }} className="text-[#64748B] hover:text-[#0052FF] transition-colors" data-testid={`edit-account-${account.account_id}`}>
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleDelete(account); }} className="text-[#64748B] hover:text-[#EF4444] transition-colors" data-testid={`delete-account-${account.account_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <h3 className="relative text-xl font-semibold text-[#020617] mb-4 group-hover:text-[#0052FF] transition-colors pointer-events-none">{account.name}</h3>
                {account.counts_toward_shortfall !== null && account.counts_toward_shortfall !== undefined && (
                  <div className="relative -mt-3 mb-3 pointer-events-none">
                    <span
                      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${
                        account.counts_toward_shortfall ? 'bg-[#FFEDD5] text-[#9A3412]' : 'bg-[#F1F5F9] text-[#475569]'
                      }`}
                      data-testid={`shortfall-badge-${account.account_id}`}
                      title={account.counts_toward_shortfall ? 'This account counts toward the shortfall warning' : 'This account is excluded from the shortfall warning'}
                    >
                      {account.counts_toward_shortfall ? 'Shortfall-tracked' : 'Shortfall-excluded'}
                    </span>
                  </div>
                )}
                <div className={`relative text-3xl font-bold pointer-events-none ${isLiability ? 'text-[#EF4444]' : 'text-[#020617]'}`} data-testid={`account-balance-${account.account_id}`}>
                  {isLiability ? '-' : ''}£{fmtMoney(account.balance)}
                </div>
                <div className="relative text-sm text-[#64748B] mt-2 pointer-events-none">
                  {isLiability ? 'Owed' : account.currency}
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(open) => { if (!open) setPendingDelete(null); }}
        title="Delete this account?"
        description={
          pendingDelete
            ? `"${pendingDelete.name}" and all of its transactions and recurring entries will be permanently removed.`
            : 'This cannot be undone.'
        }
        confirmLabel="Delete"
        onConfirm={confirmDelete}
        testId="delete-account-dialog"
      />
    </div>
  );
};

export default Accounts;
